import React from "react";
import styles from "./GenericGamepad.module.css";
import type {
  VirtualGamepadProps,
  MouseSimulationHandlers,
} from "../../constants/gamepad";
import { useVirtualGamepadInput } from "../../hooks/useVirtualGamepadInput";
import LBIcon1x from "../../assets/icons/LB@1x.webp";
import LBIcon2x from "../../assets/icons/LB@2x.webp";
import LBIcon3x from "../../assets/icons/LB@3x.webp";
import LTIcon1x from "../../assets/icons/LT@1x.webp";
import LTIcon2x from "../../assets/icons/LT@2x.webp";
import LTIcon3x from "../../assets/icons/LT@3x.webp";
import RBIcon1x from "../../assets/icons/RB@1x.webp";
import RBIcon2x from "../../assets/icons/RB@2x.webp";
import RBIcon3x from "../../assets/icons/RB@3x.webp";
import RTIcon1x from "../../assets/icons/RT@1x.webp";
import RTIcon2x from "../../assets/icons/RT@2x.webp";
import RTIcon3x from "../../assets/icons/RT@3x.webp";
import L3Icon1x from "../../assets/icons/l3@1x.webp";
import L3Icon2x from "../../assets/icons/l3@2x.webp";
import L3Icon3x from "../../assets/icons/l3@3x.webp";
import R3Icon1x from "../../assets/icons/r3@1x.webp";
import R3Icon2x from "../../assets/icons/r3@2x.webp";
import R3Icon3x from "../../assets/icons/r3@3x.webp";
import {
  ActionButtonGroup,
  Joystick,
  DPadButtonGroup,
  TopButtonContainer,
} from "../shared";
import { Button } from "../shared/Button";

interface GenericGamepadProps extends MouseSimulationHandlers {
  onStateChange?: VirtualGamepadProps["onStateChange"];
  sendMessage?: VirtualGamepadProps["sendMessage"];
  onToggleMenu?: () => void;
  isActive?: boolean;
}

export const GenericGamepad: React.FC<GenericGamepadProps> = ({
  onStateChange,
  sendMessage,
  onToggleMenu,
  isActive = false,
  shouldSimulateMouseEvents,
  simulateMouseMovement,
  simulateMouseEvent,
  stopMouseSimulation,
}) => {
  const {
    isLeftJoystickActive,
    leftJoystickOuterRef,
    isRightJoystickActive,
    rightJoystickOuterRef,
    handleButtonTouchStart,
    handleButtonTouchEnd,
    handleButtonMouseDown,
    handleButtonMouseUp,
    handleLeftJoystickTouchStart,
    handleLeftJoystickTouchMove,
    handleLeftJoystickTouchEnd,
    handleLeftJoystickMouseDown,
    leftJoystickTransform,
    handleRightJoystickTouchStart,
    handleRightJoystickTouchMove,
    handleRightJoystickTouchEnd,
    handleRightJoystickMouseDown,
    rightJoystickTransform,
  } = useVirtualGamepadInput({
    onStateChange,
    sendMessage,
    onToggleMenu,
    isActive,
    shouldSimulateMouseEvents,
    simulateMouseMovement,
    simulateMouseEvent,
    stopMouseSimulation,
  });

  if (!isActive) return null;

  return (
    <div className={styles.container}>
      <div className={styles.gamepadContainer}>
        <TopButtonContainer
          onTouchStart={handleButtonTouchStart}
          onTouchEnd={handleButtonTouchEnd}
          onMouseDown={handleButtonMouseDown}
          onMouseUp={handleButtonMouseUp}
        />
        <div className={styles.middle}>
          <DPadButtonGroup
            onTouchStart={handleButtonTouchStart}
            onTouchEnd={handleButtonTouchEnd}
            onMouseDown={handleButtonMouseDown}
            onMouseUp={handleButtonMouseUp}
          />
          <ActionButtonGroup
            onTouchStart={handleButtonTouchStart}
            onTouchEnd={handleButtonTouchEnd}
            onMouseDown={handleButtonMouseDown}
            onMouseUp={handleButtonMouseUp}
          />
        </div>
        <div className={styles.bottom}>
          <div className={styles.bottomLeft}>
            <Joystick
              isActive={isLeftJoystickActive}
              outerRef={leftJoystickOuterRef}
              onTouchStart={handleLeftJoystickTouchStart}
              onTouchMove={handleLeftJoystickTouchMove}
              onTouchEnd={handleLeftJoystickTouchEnd}
              onMouseDown={handleLeftJoystickMouseDown}
              transform={leftJoystickTransform}
            />
            <div className={styles.triggerButtonsContainer}>
              <Button
                buttonKey="l1"
                iconConfig={{
                  src1x: LBIcon1x,
                  src2x: LBIcon2x,
                  src3x: LBIcon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
                className={styles.triggerButton}
              />
              <Button
                buttonKey="l2"
                iconConfig={{
                  src1x: LTIcon1x,
                  src2x: LTIcon2x,
                  src3x: LTIcon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
                className={styles.triggerButton}
              />
            </div>
            <div className={styles.stickPressWrap}>
              <Button
                buttonKey="l3"
                iconConfig={{
                  src1x: L3Icon1x,
                  src2x: L3Icon2x,
                  src3x: L3Icon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
                className={styles.triggerButton}
              />
            </div>
          </div>
          <div className={styles.bottomRight}>
            <div className={styles.stickPressWrap}>
              <Button
                buttonKey="r3"
                iconConfig={{
                  src1x: R3Icon1x,
                  src2x: R3Icon2x,
                  src3x: R3Icon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
                className={styles.triggerButton}
              />
            </div>
            <div className={styles.triggerButtonsContainer}>
              <Button
                buttonKey="r1"
                iconConfig={{
                  src1x: RBIcon1x,
                  src2x: RBIcon2x,
                  src3x: RBIcon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
                className={styles.triggerButton}
              />
              <Button
                buttonKey="r2"
                iconConfig={{
                  src1x: RTIcon1x,
                  src2x: RTIcon2x,
                  src3x: RTIcon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
                className={styles.triggerButton}
              />
            </div>
            <Joystick
              isActive={isRightJoystickActive}
              outerRef={rightJoystickOuterRef}
              onTouchStart={handleRightJoystickTouchStart}
              onTouchMove={handleRightJoystickTouchMove}
              onTouchEnd={handleRightJoystickTouchEnd}
              onMouseDown={handleRightJoystickMouseDown}
              transform={rightJoystickTransform}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
